import { useEffect, useState } from 'react';
import {
  useParams,
  useNavigate,
  useSearchParams,
  useLocation,
} from 'react-router-dom';
import { LEARNER } from '../../../constants/nav-routes';
import { useGeneralState } from '../../../context/general-state';
import { useProgressCheck } from '../../../context/progress-check';
import { types as Ty } from '../../../constants';

import ListenAndRepeat from '../../Exercises/ExerciseStep/ListenAndRepeat';
import ListenAndRepeatGrid from '../../Exercises/ExerciseStep/ListenAndRepeatGrid';
import { replaceMediaKeysWithUrls } from '../../../helpers';
import Comprehensions from '../../Exercises/ExerciseStep/Comprehensions';
import MultipleChoice from '../../Exercises/ExerciseStep/MultipleChoice';
import {
  DragAndDropV2,
  DragAndDropCreateOrder,
} from '../../Exercises/ExerciseStep/DragAndDrop';
import CombineWords from '../../Exercises/ExerciseStep/CombineWords';
import TypingHorizontal from '../../Exercises/ExerciseStep/TypingHorizontal';
import FillInTheGrid from '../../Exercises/ExerciseStep/FillInTheGrid';
import FactFamily from '../../Exercises/ExerciseStep/FactFamily';
import Wheel from '../../Exercises/ExerciseStep/Wheel';
import Pyramid from '../../Exercises/ExerciseStep/Pyramid';
import BarChart from '../../Exercises/ExerciseStep/BarChart';
import ColumnsTyping from '../../Exercises/ExerciseStep/ColumnsTyping';
import Formulae from '../../Exercises/ExerciseStep/Formulae';
import Fractions from '../../Exercises/ExerciseStep/Fractions';
import { useCreateCompletedProgressCheck } from '../../../api-calls/progressChecks.queries';
import { keyMilestones } from '../../../constants/data-types';
import { useSiteSettings } from 'context/site-settings';

const initStep = {
  audioKey: '',
  correctOption: null,
  imageKey: '',
  options: null,
  subtitle: '',
  title: '',
};

const ProgressCheckStep = () => {
  const navigate = useNavigate();
  const { steps } = useProgressCheck();
  const { state: generalState } = useGeneralState();
  const [searchParams] = useSearchParams();
  const sandboxMode = searchParams.get('sandboxMode');
  const { stepId, milestoneId } = useParams();
  const [step, setStep] = useState(initStep);
  const { search } = useLocation();
  const { isCmidCourse } = useSiteSettings();

  const index = stepId - 1;
  const { mutate: createCompletedProgressCheck } =
    useCreateCompletedProgressCheck({
      milestoneId,
    });
  const handleNext = () => {
    if (index === steps.length - 1) {
      if (sandboxMode === 'true' || generalState?.isPlayground) {
        if (keyMilestones.includes(Number(milestoneId))) {
          navigate(
            LEARNER.PROGRESS_PROFILE_QUESTIONS.replace(':id', 1).replace(
              ':milestoneId',
              milestoneId
            ) + search
          );
        } else {
          const params = new URLSearchParams(search);
          params.set('milestoneId', milestoneId);
          navigate(LEARNER.PROGRESS_CHECK_THANK_YOU + `?${params.toString()}`);
        }
        return;
      }

      createCompletedProgressCheck(
        { milestoneId },
        {
          onSuccess: () => {
            if (keyMilestones.includes(Number(milestoneId))) {
              navigate(
                LEARNER.PROGRESS_PROFILE_QUESTIONS.replace(':id', 1).replace(
                  ':milestoneId',
                  milestoneId
                )
              );
            } else {
              const params = new URLSearchParams(search);
              params.set('milestoneId', milestoneId);
              navigate(
                LEARNER.PROGRESS_CHECK_THANK_YOU + `?${params.toString()}`
              );
            }
          },
        }
      );
    } else {
      navigate(
        LEARNER.PROGRESS_CHECK_QUESTIONS.replace(
          ':stepId',
          Number(stepId) + 1
        ).replace(':milestoneId', milestoneId) + search
      );
    }
  };
  useEffect(() => {
    if (steps?.length)
      setStep({
        ...replaceMediaKeysWithUrls(steps[index], generalState?.preferredVoice),
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [steps, stepId, milestoneId, index]);

  switch (step.type) {
    case Ty.exerciseTypes.LISTEN_AND_REPEAT:
      return <ListenAndRepeat {...step} handleNext={handleNext} />;

    case Ty.exerciseTypes.LISTEN_AND_REPEAT_GRID:
      return <ListenAndRepeatGrid {...step} handleNext={handleNext} />;
    case Ty.exerciseTypes.TYPING:
      return (
        <Comprehensions isCheck="true" handleNext={handleNext} {...step} />
      );
    case Ty.exerciseTypes.MULTIPLE_CHOICE:
      if (isCmidCourse) {
        return (
          <MultipleChoice
            key={`${stepId}-${step.data?.options?.length}`}
            handleNext={handleNext}
            data={{
              ...step.data,
              questionAudioUrl: step.data.textAudioUrl,
              question: step.data.textAudio,
            }}
            imageUrl={step.imageUrl || step.data.imageUrl}
            audioUrl={step.audioUrl || step.data.audioUrl}
          />
        );
      }
      return (
        <Comprehensions isCheck="true" handleNext={handleNext} {...step} />
      );
    case Ty.exerciseTypes.DRAG_AND_DROP_2:
      return (
        <DragAndDropV2
          key={`${stepId}-${step.data?.correctAnswer}`}
          handleNext={handleNext}
          {...step}
        />
      );
    case Ty.exerciseTypes.DRAG_AND_DROP_CREATE_ORDER:
      return (
        <DragAndDropCreateOrder
          key={`${stepId}-${step.data?.correctAnswer}`}
          handleNext={handleNext}
          {...step}
        />
      );
    case Ty.exerciseTypes.COMBINE_WORDS:
      return (
        <CombineWords
          key={`${stepId}-${step.data?.options?.length}`}
          handleNext={handleNext}
          {...step}
        />
      );
    case Ty.exerciseTypes.TYPING_HORIZONTAL:
      return (
        <TypingHorizontal
          key={`${stepId}-${step.data?.correctOptions?.length}`}
          handleNext={handleNext}
          {...step}
        />
      );
    case Ty.exerciseTypes.FILL_IN_THE_GRID:
      return (
        <FillInTheGrid
          key={`${stepId}-${step.data?.gridEnd}-${step.data?.gridStart}`}
          handleNext={handleNext}
          {...step}
        />
      );
    case Ty.exerciseTypes.FACT_FAMILY:
      return (
        <FactFamily
          key={`${stepId}-${step.data?.options?.length}`}
          handleNext={handleNext}
          {...step}
        />
      );
    case Ty.exerciseTypes.WHEEL:
      return (
        <Wheel
          key={`${stepId}-${step.data?.centerNumber}`}
          handleNext={handleNext}
          {...step}
        />
      );
    case Ty.exerciseTypes.PYRAMID:
      return (
        <Pyramid
          key={`${stepId}-${step.data?.options?.length}`}
          handleNext={handleNext}
          {...step}
        />
      );
    case Ty.exerciseTypes.BAR_CHART:
      return (
        <BarChart
          key={`${stepId}-${step.data?.question}`}
          handleNext={handleNext}
          {...step}
        />
      );
    case Ty.exerciseTypes.COLUMNS_TYPING:
      return (
        <ColumnsTyping
          key={`${stepId}-${step.id}`}
          handleNext={handleNext}
          {...step}
        />
      );
    case Ty.exerciseTypes.FORMULAE:
      return (
        <Formulae
          key={`${stepId}-${step.data?.formulaType}`}
          handleNext={handleNext}
          {...step}
        />
      );
    case Ty.exerciseTypes.FRACTIONS:
      return (
        <Fractions
          key={`${stepId}-${step.data?.correctAnswer}`}
          handleNext={handleNext}
          {...step}
        />
      );
    default:
      return null;
  }
};

export default ProgressCheckStep;
